"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { db } from "@/lib/db";
import { useUser } from "@/lib/user/context";
import { getUserAIs } from "@/lib/ai/entityManager";
import { entityToAiProfile } from "@/types/aiEntity";
import type { AiProfile } from "@/types/ai";
import type { Team, TeamMember } from "@/types/collaboration";
import { useLoadingData } from "@/lib/hooks/useLoadingData";
import TeamWorkspace from "@/components/team/TeamWorkspace";
import PremiumHeroBackground from "@/components/ui/PremiumHeroBackground";

async function loadTeamData(user: { id: string } | null): Promise<{ team: Team | null; userAIs: AiProfile[] }> {
  if (!user || typeof window === "undefined" || !db) return { team: null, userAIs: [] };
  const ais = await getUserAIs(user.id);
  const userAIs = ais.map(entityToAiProfile);
  let team: Team | null = db.teams ? await db.teams.where("ownerId").equals(user.id).first() : null;
  if (!team && db.teams) {
    const displayName = (user as any).user_metadata?.full_name || (user as any).user_metadata?.name || (user as any).email || "User";
    const teamId = crypto.randomUUID();
    const newTeam: Team = {
      id: teamId,
      name: `${displayName}'s Team`,
      ownerId: user.id,
      profileIds: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    await db.teams.add(newTeam);
    if (db.teamMembers) {
      const teamMember: TeamMember = {
        id: crypto.randomUUID(),
        teamId,
        userId: user.id,
        email: (user as any).email || `${user.id}@yourdreamai.local`,
        permission: "admin",
        invitedAt: Date.now(),
        joinedAt: Date.now(),
        invitedBy: user.id,
      };
      await db.teamMembers.add(teamMember);
    }
    team = newTeam;
  }
  return { team, userAIs };
}

export default function TeamPage() {
  const router = useRouter();
  const { user } = useUser();
  const { data, loading } = useLoadingData(() => loadTeamData(user), [user?.id]);
  const team = data?.team ?? null;
  const userAIs = data?.userAIs ?? [];
  const [teamMembers, setTeamMembersState] = useState<TeamMember[]>([]);

  useEffect(() => {
    if (!user) router.push("/start");
  }, [user, router]);

  useEffect(() => {
    if (team && typeof window !== "undefined" && db?.teamMembers) {
      db.teamMembers.where("teamId").equals(team.id).toArray().then(setTeamMembersState).catch(console.error);
    }
  }, [team]);

  if (loading) {
    return (
      <PremiumHeroBackground className="min-h-screen flex items-center justify-center">
        <div className="text-white">Loading team workspace...</div>
      </PremiumHeroBackground>
    );
  }

  if (!team || !user) {
    return (
      <PremiumHeroBackground className="min-h-screen flex items-center justify-center">
        <div className="text-center text-white">
          <p className="mb-4">Unable to load team workspace</p>
          <button
            onClick={() => router.push("/start")}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition"
          >
            Go Back
          </button>
        </div>
      </PremiumHeroBackground>
    );
  }

  return (
    <PremiumHeroBackground className="min-h-screen">
      <TeamWorkspace
        team={team}
        currentUserId={user.id}
        userAIs={userAIs}
        teamMemberAIs={[]} // TODO: Load other team members' AIs
        teamMembers={teamMembers}
      />
    </PremiumHeroBackground>
  );
}
